import { normalizeVouchers, PersonBenefits, PortalServiceV1 } from '@esportal/portal-service';
import { isVehicle } from '../../../helpers/common-helpers';
import { AlertType, CombinedAlert, VoucherToAlertMap } from '../alerts-type';
import {
	getPromotionByName,
	isPersonWinnerInPromotion,
	personParticipatedInPromotion,
	isPromotionOpened,
	isPersonNotWinnerInPromotion,
	isPromotionFull,
} from '../../../helpers/promotions-helper';
import { useMemo } from 'react';

export const useAlertsData = (personBenefits: PersonBenefits, portalService: PortalServiceV1) => {
	const vehiclesData = useMemo(() => portalService.getPersonVehicles().getValue(), [portalService]);
	const promotions = personBenefits?.benefits.promotions;

	const filteredFirstThreeAlerts = useMemo(() => {
		const localAlerts: CombinedAlert[] = [];
		const normalizedVouchers = normalizeVouchers(personBenefits);

		// 1. Procesar Voucher Alerts
		normalizedVouchers.forEach((voucher) => {
			if (voucher.campaign && VoucherToAlertMap[voucher.campaign]) {
				const alertType = VoucherToAlertMap[voucher.campaign];

				if (voucher.vin) {
					const vehicleInfo = portalService.getVehicleOrOrderByVin(voucher.vin).getValue();
					if (isVehicle(vehicleInfo)) {
						localAlerts.push({ alertType, voucher, vehicle: vehicleInfo, priority: voucher.priority });
					} else {
						localAlerts.push({ alertType, voucher, priority: voucher.priority });
					}
				} else {
					localAlerts.push({ alertType, voucher, priority: voucher.priority });
				}
			}
		});

		// 2. Procesar Promociones
		if (promotions && promotions.length > 0) {
			const mastersPromotion = getPromotionByName(promotions, 'MastersCarretera2026');
			const laLigaPromotion = getPromotionByName(promotions, 'LaLigaLanzamiento2026');
			const elClasicoPromotion = getPromotionByName(promotions, 'ElClasico2026');
			const gtiDay26Promotion = getPromotionByName(promotions, 'GTIDay2026');
			const gtiDayVIP26Promotion = getPromotionByName(promotions, 'GTIDayVIP2026');

			if (mastersPromotion) {
				if (isPersonWinnerInPromotion(mastersPromotion)) {
					localAlerts.push({ alertType: AlertType.Masters26_Winner, priority: mastersPromotion.priority });
				} else if (personParticipatedInPromotion(mastersPromotion)) {
					localAlerts.push({ alertType: AlertType.Masters26_Enrolled, priority: mastersPromotion.priority });
				} else if (isPromotionOpened(mastersPromotion)) {
					localAlerts.push({ alertType: AlertType.Masters26_Open, priority: mastersPromotion.priority });
				}
			}

			if (laLigaPromotion) {
				if (isPersonNotWinnerInPromotion(laLigaPromotion)) {
					localAlerts.push({ alertType: AlertType.LaLiga26_NoWinner, priority: laLigaPromotion.priority });
				} else if (personParticipatedInPromotion(laLigaPromotion)) {
					localAlerts.push({ alertType: AlertType.LaLiga26_Enrolled, priority: laLigaPromotion.priority });
				} else if (isPromotionOpened(laLigaPromotion)) {
					localAlerts.push({ alertType: AlertType.LaLiga26_Open, priority: laLigaPromotion.priority });
				}
			}

			if (elClasicoPromotion) {
				if (isPersonNotWinnerInPromotion(elClasicoPromotion)) {
					localAlerts.push({ alertType: AlertType.ElClasico26_NoWinner, priority: elClasicoPromotion.priority });
				} else if (personParticipatedInPromotion(elClasicoPromotion)) {
					localAlerts.push({ alertType: AlertType.ElClasico26_Enrolled, priority: elClasicoPromotion.priority });
				} else if (isPromotionOpened(elClasicoPromotion)) {
					localAlerts.push({ alertType: AlertType.ElClasico26_Open, priority: elClasicoPromotion.priority });
				}
			}

			if (gtiDay26Promotion) {
				if (personParticipatedInPromotion(gtiDay26Promotion)) {
					localAlerts.push({ alertType: AlertType.GTIDay26_Enrolled, priority: gtiDay26Promotion.priority });
				} else if (isPromotionOpened(gtiDay26Promotion)) {
					localAlerts.push({ alertType: AlertType.GTIDay26_Open, priority: gtiDay26Promotion.priority });
				}
			}

			if (gtiDayVIP26Promotion) {
				if (personParticipatedInPromotion(gtiDayVIP26Promotion)) {
					localAlerts.push({ alertType: AlertType.GTIDayVIP26_Enrolled, priority: gtiDayVIP26Promotion.priority });
				} else if (isPromotionFull(gtiDayVIP26Promotion)) {
					localAlerts.push({ alertType: AlertType.GTIDayVIP26_Full, priority: gtiDayVIP26Promotion.priority });
				} else if (isPromotionOpened(gtiDayVIP26Promotion)) {
					localAlerts.push({ alertType: AlertType.GTIDayVIP26_Open, priority: gtiDayVIP26Promotion.priority });
				}
			}
		}

		// 3. Procesar Vehicle Alerts
		if (vehiclesData?.vehicles) {
			vehiclesData.vehicles.forEach((vehicle) => {
				if (vehicle.alertNextMaintenance) {
					localAlerts.push({ alertType: AlertType.Mantenimiento, vehicle });
				}
				if (vehicle.alertPreITV) {
					localAlerts.push({ alertType: AlertType.PreITV, vehicle });
				}
			});
		}

		const sortedAlerts = localAlerts.sort((a, b) => (b.priority || 0) - (a.priority || 0)); // a mayor número -> mayor prioridad
		return sortedAlerts.slice(0, 3);
	}, [personBenefits, vehiclesData, promotions, portalService]);

	return filteredFirstThreeAlerts;
};
